import React from "react";
import { Box, Typography, Chip, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import CommonModal from "../common/CommonModal";
import { Blog } from "../../types/blogs";

interface BlogDetailModalProps {
  open: boolean;
  onClose: () => void;
  blog?: Blog;
  loading?: boolean;
}

const formatDateDdMmYyyy = (value: any): string => {
  if (!value) return "";

  if (Array.isArray(value)) {
    const [y, m, d] = value;
    return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
  }

  if (typeof value === "string") {
    if (value.includes(",")) {
      const parts = value.split(",").map(Number);
      if (parts.length >= 3) {
        const [y, m, d] = parts;
        return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
      }
    }
    const dt = new Date(value);
    if (!Number.isNaN(dt.getTime())) {
      const d = dt.getDate();
      const m = dt.getMonth() + 1;
      const y = dt.getFullYear();
      return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
    }
  }

  return "";
};

const BlogDetailModal: React.FC<BlogDetailModalProps> = ({ open, onClose, blog, loading }) => {
  const { t } = useTranslation();

  const formattedDate = blog ? formatDateDdMmYyyy(blog.publishedAt || blog.issueDate) : "";

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={blog?.title || t("landing_blogs_read_more") || "Read Article"}
      maxWidth="md"
      fullWidth
      hideActions
    >
      {loading || !blog ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {blog.coverImageUrl && (
            <Box
              component="img"
              src={blog.coverImageUrl}
              alt={blog.title}
              sx={{
                width: "100%",
                maxHeight: 360,
                objectFit: "cover",
                borderRadius: "12px",
                mb: 3,
              }}
            />
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Chip
              label={blog.category}
              size="small"
              sx={{
                background: "#EFF6FF",
                color: "#1D4ED8",
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            />
            {formattedDate && (
              <Typography sx={{ fontSize: "0.8rem", color: "#64748B" }}>
                {formattedDate}
              </Typography>
            )}
          </Box>
          <Typography
            sx={{
              fontFamily: "'Playfair Display',serif",
              fontSize: "1.5rem",
              fontWeight: 700,
              color: "#0B1D6E",
              mb: 2,
            }}
          >
            {blog.title}
          </Typography>
          <Typography sx={{ fontSize: "0.95rem", color: "#475569", mb: 3, lineHeight: 1.7 }}>
            {blog.description}
          </Typography>
          <Box
            sx={{
              fontSize: "0.925rem",
              color: "#1E293B",
              lineHeight: 1.85,
              "& h1, & h2, & h3": {
                fontFamily: "'Playfair Display',serif",
                color: "#0B1D6E",
                mt: 3,
                mb: 1.5,
              },
              "& p": { mb: 1.5 },
              "& ul, & ol": { pl: 3, mb: 1.5 },
              "& img": { maxWidth: "100%", borderRadius: "8px", my: 2 },
              "& a": { color: "#1D4ED8" },
            }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(blog.content || "") }}
          />
        </Box>
      )}
    </CommonModal>
  );
};

export default BlogDetailModal;
